import { useRef, useState, useCallback, useEffect } from 'react';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
} from 'amazon-chime-sdk-js';
import CLINIC_CONFIG from '../../../config/clinicConfig';

export type CallState = 'idle' | 'connecting' | 'active' | 'ending' | 'error';

export interface TranscriptEntry {
  role: 'you' | 'ai';
  text: string;
  interim?: boolean;
}

export interface UseVoiceCallReturn {
  callState: CallState;
  isMuted: boolean;
  callDuration: number;
  transcript: TranscriptEntry[];
  startCall: () => Promise<void>;
  endCall: () => void;
  toggleMute: () => void;
  error: string | null;
}

const VOICE_API_URL = CLINIC_CONFIG.voiceApiUrl || '';

const SpeechRecognitionAPI: any =
  typeof window !== 'undefined'
    ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    : undefined;

export function useVoiceCall(): UseVoiceCallReturn {
  const [callState, setCallState] = useState<CallState>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);

  const meetingSessionRef = useRef<DefaultMeetingSession | null>(null);
  const contactIdRef = useRef<string | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const isUnmountedRef = useRef(false);
  const recognitionRef = useRef<any>(null);
  // Tracks whether the bot (remote attendee) is currently speaking via Chime volume indicators
  const botSpeakingRef = useRef(false);
  const selfAttendeeIdRef = useRef<string>('');

  const stopRecognition = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* already stopped */ }
      recognitionRef.current = null;
    }
  }, []);

  const startRecognition = useCallback(() => {
    if (!SpeechRecognitionAPI) return;
    stopRecognition();

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event: any) => {
      if (isUnmountedRef.current) return;

      let interimText = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }

      // Attribute to bot or user based on who is currently speaking
      const role: 'you' | 'ai' = botSpeakingRef.current ? 'ai' : 'you';

      if (finalText.trim()) {
        setTranscript(prev => {
          const filtered = prev.filter(e => !e.interim);
          return [...filtered, { role, text: finalText.trim() }];
        });
      } else if (interimText.trim()) {
        setTranscript(prev => {
          const filtered = prev.filter(e => !e.interim);
          return [...filtered, { role, text: interimText.trim(), interim: true }];
        });
      }
    };

    recognition.onerror = (event: any) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.warn('[SpeechRecognition] error:', event.error);
    };

    recognition.onend = () => {
      if (!isUnmountedRef.current && recognitionRef.current === recognition) {
        try { recognition.start(); } catch { /* ignore restart errors */ }
      }
    };

    try { recognition.start(); } catch { /* already running */ }
  }, [stopRecognition]);

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    stopRecognition();

    const session = meetingSessionRef.current;
    if (session) {
      try {
        session.audioVideo.stop();
      } catch { /* already stopped */ }
      meetingSessionRef.current = null;
    }
  }, [stopRecognition]);

  const startCall = useCallback(async () => {
    if (!VOICE_API_URL) {
      setError('Voice calling is not configured');
      setCallState('error');
      return;
    }

    if (callState !== 'idle' && callState !== 'error') return;

    setCallState('connecting');
    setError(null);
    setCallDuration(0);
    setIsMuted(false);
    setTranscript([]);
    botSpeakingRef.current = false;

    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStream.getTracks().forEach(t => t.stop());
      if (isUnmountedRef.current) return;

      const res = await fetch(`${VOICE_API_URL}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clinicId: CLINIC_CONFIG.clinicId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server error ${res.status}`);
      }

      const data = await res.json();
      if (isUnmountedRef.current) return;

      contactIdRef.current = data.contactId;

      if (!data.meeting || !data.attendee) {
        throw new Error('Missing meeting/attendee data from server');
      }

      const logger = new ConsoleLogger('VoiceCall', LogLevel.WARN);
      const deviceController = new DefaultDeviceController(logger);

      const config = new MeetingSessionConfiguration(data.meeting, data.attendee);
      const meetingSession = new DefaultMeetingSession(config, logger, deviceController);
      meetingSessionRef.current = meetingSession;
      selfAttendeeIdRef.current = data.attendee.AttendeeId;

      const audioVideo = meetingSession.audioVideo;

      const audioInputDevices = await audioVideo.listAudioInputDevices();
      if (audioInputDevices.length > 0) {
        await audioVideo.startAudioInput(audioInputDevices[0].deviceId);
      }

      const audioOutputDevices = await audioVideo.listAudioOutputDevices();
      if (audioOutputDevices.length > 0) {
        await audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
      }

      const audioElement = document.createElement('audio');
      audioElement.autoplay = true;
      audioElement.style.display = 'none';
      document.body.appendChild(audioElement);
      audioVideo.bindAudioElement(audioElement);

      // Subscribe to volume indicators to detect bot speech
      audioVideo.realtimeSubscribeToVolumeIndicator(
        '', // empty string = all attendees
        (attendeeId: string, volume: number | null, _muted: boolean | null, _signalStrength: number | null) => {
          if (isUnmountedRef.current) return;
          // If the speaking attendee is NOT us, it's the bot
          if (attendeeId !== selfAttendeeIdRef.current) {
            botSpeakingRef.current = (volume !== null && volume > 0.01);
          }
        }
      );

      audioVideo.addObserver({
        audioVideoDidStart: () => {
          if (isUnmountedRef.current) return;
          setCallState('active');
          startRecognition();
          durationIntervalRef.current = window.setInterval(() => {
            if (!isUnmountedRef.current) setCallDuration(d => d + 1);
          }, 1000);
        },
        audioVideoDidStop: (sessionStatus) => {
          if (isUnmountedRef.current) return;
          const code = sessionStatus.statusCode();
          if (code !== MeetingSessionStatusCode.OK && code !== MeetingSessionStatusCode.Left) {
            setError('Call ended unexpectedly');
            setCallState('error');
          } else {
            setCallState('idle');
          }

          const boundEl = document.querySelector('audio[autoplay]');
          if (boundEl) boundEl.remove();

          cleanup();
          setCallDuration(0);
        },
      });

      audioVideo.start();

    } catch (err: unknown) {
      if (!isUnmountedRef.current) {
        const e = err as Error & { name?: string };
        const message = e.name === 'NotAllowedError'
          ? 'Microphone access denied. Please allow mic access and try again.'
          : e.message || 'Failed to start call';
        setError(message);
        setCallState('error');
        cleanup();
      }
    }
  }, [cleanup, callState, startRecognition]);

  const endCall = useCallback(async () => {
    setCallState('ending');

    if (contactIdRef.current && VOICE_API_URL) {
      try {
        await fetch(`${VOICE_API_URL}/stop`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId: contactIdRef.current }),
        });
      } catch { /* best-effort disconnect */ }
      contactIdRef.current = null;
    }

    const boundEl = document.querySelector('audio[autoplay]');
    if (boundEl) boundEl.remove();

    cleanup();
    setCallState('idle');
    setCallDuration(0);
  }, [cleanup]);

  const toggleMute = useCallback(() => {
    const session = meetingSessionRef.current;
    if (!session) return;

    if (isMuted) {
      session.audioVideo.realtimeUnmuteLocalAudio();
      setIsMuted(false);
    } else {
      session.audioVideo.realtimeMuteLocalAudio();
      setIsMuted(true);
    }
  }, [isMuted]);

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  return { callState, isMuted, callDuration, transcript, startCall, endCall, toggleMute, error };
}
